import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Plug,
  Bell,
  Radio,
  Camera,
  UserCheck,
  Flame,
  DoorOpen,
  Users,
  Video,
  Siren,
  RefreshCw,
  Save,
  Send,
  Download,
  CheckCircle2,
  Key,
} from "lucide-react";
import { toast } from "react-toastify";
import { SpinLoader } from "../../../components/loader/Loader";
import {
  getSecurityNotificationLogs,
  getSecurityIntegrationOverview,
  getSecurityIntegrationSettings,
  updateSecurityIntegrationSettings,
  testSecurityIntegrationEventPush,
  validateVisitorPassIntegration,
  exportSecurityIncidentsForSync,
  acknowledgeSecurityIncidentSyncWatermark,
  SecurityIntegrationOverview,
  SecurityIntegrationSettings,
  IntegrationConnectorConfig,
  IntegrationConnectorStatus,
  getAccessControlOverview,
  AccessControlOverview,
} from "../../../service/securityService";

const CONNECTOR_DEFS: {
  id: string;
  title: string;
  icon: React.ElementType;
}[] = [
  { id: "fire", title: "Fire systems", icon: Flame },
  { id: "access_hw", title: "Access control hardware", icon: DoorOpen },
  { id: "hr", title: "HR systems", icon: Users },
  { id: "vms", title: "VMS", icon: Video },
  { id: "emergency", title: "Emergency response systems", icon: Siren },
  {
    id: "messaging_gateways",
    title: "SMS / WhatsApp / Email gateways",
    icon: Bell,
  },
];

const STATUS_OPTIONS: { value: string; label: string }[] = [
  { value: "partner_setup", label: "Partner / field setup" },
  { value: "in_progress", label: "In progress" },
  { value: "live", label: "Live" },
  { value: "paused", label: "Paused" },
  { value: "api_ready", label: "API ready (platform)" },
];

function statusBadgeClass(status?: string) {
  if (status === "live" || status === "api_ready")
    return "text-emerald-800 bg-emerald-50";
  if (status === "in_progress") return "text-blue-800 bg-blue-50";
  if (status === "paused") return "text-gray-700 bg-gray-100";
  return "text-amber-800 bg-amber-50";
}

const SecurityIntegrations: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [logs, setLogs] = useState<Record<string, unknown>[]>([]);
  const [overview, setOverview] = useState<SecurityIntegrationOverview | null>(
    null
  );
  const [settings, setSettings] = useState<SecurityIntegrationSettings | null>(
    null
  );
  const [eventSecretDraft, setEventSecretDraft] = useState("");
  const [passIdNumber, setPassIdNumber] = useState("");
  const [passMobile, setPassMobile] = useState("");
  const [passResult, setPassResult] = useState<{
    valid: boolean;
    reasons: string[];
    visitor: Record<string, unknown> | null;
  } | null>(null);
  const [passBusy, setPassBusy] = useState(false);
  const [exportBusy, setExportBusy] = useState(false);
  const [exportPreview, setExportPreview] = useState<number | null>(null);
  const [testPushBusy, setTestPushBusy] = useState(false);
  const [accessOverview, setAccessOverview] =
    useState<AccessControlOverview | null>(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [logRes, ov, st, ac] = await Promise.all([
        getSecurityNotificationLogs({ limit: 40 }),
        getSecurityIntegrationOverview(),
        getSecurityIntegrationSettings(),
        getAccessControlOverview(),
      ]);
      setLogs(logRes.data);
      setOverview(ov);
      setSettings(st);
      setAccessOverview(ac);
    } catch (e) {
      console.error(e);
      toast.error("Failed to load integration data");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const patchConnector = (
    id: string,
    patch: Partial<IntegrationConnectorConfig>
  ) => {
    setSettings((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        connectors: {
          ...prev.connectors,
          [id]: { ...(prev.connectors[id] || {}), ...patch },
        },
      };
    });
  };

  const saveConnectors = async () => {
    if (!settings) return;
    setSaving(true);
    try {
      const updated = await updateSecurityIntegrationSettings({
        connectors: settings.connectors,
      });
      if (updated) setSettings(updated);
      toast.success("Connector settings saved");
      const ov = await getSecurityIntegrationOverview();
      setOverview(ov);
    } catch {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  const saveEventPush = async () => {
    if (!settings) return;
    setSaving(true);
    try {
      const payload: Parameters<typeof updateSecurityIntegrationSettings>[0] = {
        eventPush: {
          enabled: settings.eventPush.enabled,
          webhookUrl: settings.eventPush.webhookUrl,
        },
      };
      if (eventSecretDraft.trim()) {
        payload.eventPush!.sharedSecret = eventSecretDraft.trim();
      }
      const updated = await updateSecurityIntegrationSettings(payload);
      if (updated) setSettings(updated);
      setEventSecretDraft("");
      toast.success("Event push settings saved");
      const ov = await getSecurityIntegrationOverview();
      setOverview(ov);
    } catch {
      toast.error("Could not save event push");
    } finally {
      setSaving(false);
    }
  };

  const runTestPush = async () => {
    setTestPushBusy(true);
    try {
      await testSecurityIntegrationEventPush();
      toast.success("Test event sent");
      const ov = await getSecurityIntegrationOverview();
      setOverview(ov);
    } catch {
      toast.error("Test push failed — check URL, secret, and partner endpoint");
    } finally {
      setTestPushBusy(false);
    }
  };

  const runValidatePass = async (e: React.FormEvent) => {
    e.preventDefault();
    setPassBusy(true);
    setPassResult(null);
    try {
      const res = await validateVisitorPassIntegration({
        idNumber: passIdNumber.trim() || undefined,
        mobileNumber: passMobile.trim() || undefined,
      });
      setPassResult(
        res || { valid: false, reasons: ["No response"], visitor: null }
      );
    } catch {
      toast.error("Validation request failed");
    } finally {
      setPassBusy(false);
    }
  };

  const runExportPreview = async () => {
    setExportBusy(true);
    setExportPreview(null);
    try {
      const since = settings?.incidentSync?.lastWatermarkIso || undefined;
      const data = await exportSecurityIncidentsForSync({
        since: since || undefined,
        limit: 50,
      });
      setExportPreview(data?.count ?? 0);
      toast.success(
        data?.count != null
          ? `Preview: ${data.count} incident(s) in batch`
          : "No data"
      );
    } catch {
      toast.error("Incident export preview failed");
    } finally {
      setExportBusy(false);
    }
  };

  const savePartnerNameAndWatermark = async () => {
    if (!settings) return;
    setSaving(true);
    try {
      await updateSecurityIntegrationSettings({
        incidentSync: {
          partnerSystemName: settings.incidentSync.partnerSystemName || "",
        },
      });
      await acknowledgeSecurityIncidentSyncWatermark();
      const [st, ov] = await Promise.all([
        getSecurityIntegrationSettings(),
        getSecurityIntegrationOverview(),
      ]);
      if (st) setSettings(st);
      if (ov) setOverview(ov);
      toast.success("Partner name and sync watermark saved");
    } catch {
      toast.error("Could not save sync settings");
    } finally {
      setSaving(false);
    }
  };

  const camTotal = overview?.cameras.total ?? null;
  const camActive = overview?.cameras.active ?? null;

  return (
    <div className="px-2 py-4 w-full h-full mt-10 space-y-6">
      <div className="flex flex-wrap justify-between items-start gap-4">
        <div className="flex items-center gap-3">
          <div className="bg-[#EAFBFA] p-3 rounded-lg">
            <Plug className="w-7 h-7 text-[#0fa9a6]" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              Security Integration {"&"} Interoperability
            </h1>
            <p className="text-gray-600 text-sm max-w-2xl">
              Configure field connectors, gateway traffic, outbound event push,
              incident export for SOAR, camera health, and visitor pass checks—all
              tenant-scoped and authenticated.
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={() => load()}
          className="inline-flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-full text-sm hover:bg-gray-50"
        >
          <RefreshCw className="w-4 h-4" />
          Refresh
        </button>
      </div>

      <div className="bg-white rounded-lg shadow-sm p-5 border border-gray-200 flex flex-wrap justify-between gap-4 items-center">
        <div className="flex items-start gap-3">
          <div className="p-2 rounded-lg bg-teal-50">
            <Key className="w-6 h-6 text-[#0fa9a6]" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-gray-900">
              Physical access control
            </h2>
            <p className="text-xs text-gray-500 max-w-xl mt-1">
              Zones, assignments, and access logs are tenant-scoped. Wire Wiegand
              / OSDP / vendor controllers to POST access events and keep zone IDs
              aligned with the Access Zones master.
            </p>
            <div className="flex flex-wrap gap-3 mt-2 text-sm text-gray-700">
              <span>
                Zones:{" "}
                <strong>{accessOverview?.zones.total ?? "—"}</strong> (
                {accessOverview?.zones.active ?? 0} active)
              </span>
              <span>
                Assignments in window:{" "}
                <strong>{accessOverview?.assignments.activeNow ?? "—"}</strong>
              </span>
              <span>
                Pending approval:{" "}
                <strong>
                  {accessOverview?.assignments.pendingApproval ?? "—"}
                </strong>
              </span>
              <span>
                Logs 7d:{" "}
                <strong className="text-emerald-700">
                  {accessOverview?.accessLogsLast7d.granted ?? 0}
                </strong>{" "}
                granted /{" "}
                <strong className="text-red-600">
                  {accessOverview?.accessLogsLast7d.denied ?? 0}
                </strong>{" "}
                denied
              </span>
            </div>
          </div>
        </div>
        <button
          type="button"
          onClick={() => navigate("/security/access-control")}
          className="px-4 py-2 bg-gray-900 text-white rounded-lg text-sm hover:bg-gray-800 shrink-0"
        >
          Open Access Control
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg shadow-sm p-4 border border-gray-100">
          <Bell className="w-6 h-6 text-[#0fa9a6] mb-2" />
          <h3 className="font-semibold text-gray-900 text-sm">
            SMS / WhatsApp / Email gateways
          </h3>
          <p className="text-xs text-gray-500 mt-1">
            Notifications last 7 days:{" "}
            <strong>{overview?.messaging.notificationsLast7d ?? "—"}</strong>
            {overview?.messaging.byChannel &&
              Object.keys(overview.messaging.byChannel).length > 0 && (
                <span className="block mt-1 text-[11px] text-gray-400">
                  {Object.entries(overview.messaging.byChannel)
                    .slice(0, 4)
                    .map(([k, v]) => `${k}: ${v}`)
                    .join(" · ")}
                </span>
              )}
          </p>
          <span
            className={`inline-block mt-2 text-xs font-medium px-2 py-0.5 rounded ${statusBadgeClass(
              settings?.connectors?.messaging_gateways?.status
            )}`}
          >
            {settings?.connectors?.messaging_gateways?.status || "api_ready"}
          </span>
        </div>

        <div className="bg-white rounded-lg shadow-sm p-4 border border-gray-100">
          <Radio className="w-6 h-6 text-[#0fa9a6] mb-2" />
          <h3 className="font-semibold text-gray-900 text-sm">Event push</h3>
          <p className="text-xs text-gray-500 mt-1">
            {overview?.eventPush.enabled
              ? "Outbound webhook enabled."
              : "Configure webhook below; new security incidents trigger push when enabled."}
            {overview?.eventPush.lastDeliveryStatus && (
              <span className="block mt-1">
                Last: {overview.eventPush.lastDeliveryStatus}
                {overview.eventPush.lastError
                  ? ` — ${overview.eventPush.lastError}`
                  : ""}
              </span>
            )}
          </p>
          <span
            className={`inline-block mt-2 text-xs font-medium px-2 py-0.5 rounded ${
              overview?.eventPush.webhookUrlConfigured
                ? "text-emerald-800 bg-emerald-50"
                : "text-gray-600 bg-gray-100"
            }`}
          >
            {overview?.eventPush.webhookUrlConfigured
              ? "Webhook configured"
              : "Not configured"}
          </span>
        </div>

        <div className="bg-white rounded-lg shadow-sm p-4 border border-gray-100">
          <Camera className="w-6 h-6 text-[#0fa9a6] mb-2" />
          <h3 className="font-semibold text-gray-900 text-sm">
            Camera health status
          </h3>
          <p className="text-xs text-gray-500 mt-1">
            {camTotal != null ? (
              <>
                {camActive}/{camTotal} active · maint{" "}
                {overview?.cameras.maintenance ?? 0} · other{" "}
                {overview?.cameras.other ?? 0}
              </>
            ) : (
              "Loading…"
            )}
          </p>
          <span className="inline-block mt-2 text-xs font-medium text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded">
            Live from camera master
          </span>
        </div>

        <div className="bg-white rounded-lg shadow-sm p-4 border border-gray-100">
          <UserCheck className="w-6 h-6 text-[#0fa9a6] mb-2" />
          <h3 className="font-semibold text-gray-900 text-sm">
            Visitor pass validation
          </h3>
          <p className="text-xs text-gray-500 mt-1">
            On-site now (status Active):{" "}
            <strong>{overview?.visitors.checkedInNow ?? "—"}</strong>
          </p>
          <span className="inline-block mt-2 text-xs font-medium text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded">
            API below
          </span>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-5 space-y-4">
          <h2 className="text-lg font-semibold text-gray-900">
            Event push (outbound)
          </h2>
          <p className="text-xs text-gray-500">
            HTTPS endpoint that receives JSON for events such as new security
            incidents. Optional shared secret signs payloads (HMAC-SHA256, header{" "}
            <code className="bg-gray-100 px-1 rounded text-[10px]">
              X-EFM-Signature
            </code>
            ).
          </p>
          {settings && (
            <>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  className="rounded border-gray-300"
                  checked={settings.eventPush.enabled}
                  onChange={(e) =>
                    setSettings({
                      ...settings,
                      eventPush: {
                        ...settings.eventPush,
                        enabled: e.target.checked,
                      },
                    })
                  }
                />
                Enable outbound event push
              </label>
              <input
                className="w-full border rounded-lg px-3 py-2 text-sm"
                placeholder="Webhook URL"
                value={settings.eventPush.webhookUrl}
                onChange={(e) =>
                  setSettings({
                    ...settings,
                    eventPush: {
                      ...settings.eventPush,
                      webhookUrl: e.target.value,
                    },
                  })
                }
              />
              <div className="text-xs text-gray-500">
                {settings.eventPush.sharedSecretSet
                  ? `Secret on file (ends …${settings.eventPush.sharedSecretLast4 || "****"})`
                  : "No signing secret set"}
              </div>
              <input
                type="password"
                className="w-full border rounded-lg px-3 py-2 text-sm"
                placeholder="New shared secret (optional)"
                value={eventSecretDraft}
                onChange={(e) => setEventSecretDraft(e.target.value)}
                autoComplete="new-password"
              />
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  disabled={saving}
                  onClick={saveEventPush}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-gray-900 text-white rounded-lg text-sm hover:bg-gray-800 disabled:opacity-50"
                >
                  <Save className="w-4 h-4" />
                  Save
                </button>
                <button
                  type="button"
                  disabled={testPushBusy || saving}
                  onClick={runTestPush}
                  className="inline-flex items-center gap-2 px-4 py-2 border border-[#0fa9a6] text-[#0fa9a6] rounded-lg text-sm hover:bg-teal-50 disabled:opacity-50"
                >
                  <Send className="w-4 h-4" />
                  Send test event
                </button>
              </div>
            </>
          )}
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-5 space-y-4">
          <h2 className="text-lg font-semibold text-gray-900">Incident sync</h2>
          <p className="text-xs text-gray-500">
            Pull security incidents incrementally for SOAR or ticketing. Use the
            export preview, then advance the watermark after your system
            acknowledges rows.
          </p>
          <div className="text-sm text-gray-700">
            <p>
              Last 24h new incidents:{" "}
              <strong>{overview?.incidents.createdLast24h ?? "—"}</strong>
            </p>
            <p>
              Last 7d:{" "}
              <strong>{overview?.incidents.createdLast7d ?? "—"}</strong>
            </p>
            <p className="text-xs text-gray-500 mt-2">
              Watermark:{" "}
              {settings?.incidentSync?.lastWatermarkIso || "Not set"}
            </p>
          </div>
          {settings && (
            <input
              className="w-full border rounded-lg px-3 py-2 text-sm"
              placeholder="Partner system name (e.g. SOAR)"
              value={settings.incidentSync.partnerSystemName || ""}
              onChange={(e) =>
                setSettings({
                  ...settings,
                  incidentSync: {
                    ...settings.incidentSync,
                    partnerSystemName: e.target.value,
                  },
                })
              }
            />
          )}
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              disabled={exportBusy}
              onClick={runExportPreview}
              className="inline-flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50 disabled:opacity-50"
            >
              <Download className="w-4 h-4" />
              {exportBusy ? "Loading…" : "Preview export batch"}
            </button>
            {exportPreview != null && (
              <span className="text-sm text-gray-600 self-center">
                {exportPreview} row(s)
              </span>
            )}
            <button
              type="button"
              disabled={saving}
              onClick={savePartnerNameAndWatermark}
              className="inline-flex items-center gap-2 px-4 py-2 bg-[#0fa9a6] text-white rounded-lg text-sm hover:bg-teal-600 disabled:opacity-50"
            >
              <CheckCircle2 className="w-4 h-4" />
              Save partner name &amp; set watermark to now
            </button>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-5">
        <h2 className="text-lg font-semibold text-gray-900 mb-2">
          Validate visitor pass
        </h2>
        <p className="text-xs text-gray-500 mb-3">
          Match by government ID number or mobile as stored on the visitor
          record; validates visit window and blacklist state.
        </p>
        <form
          onSubmit={runValidatePass}
          className="flex flex-wrap gap-2 items-end"
        >
          <div>
            <label className="text-xs text-gray-500 block mb-1">ID number</label>
            <input
              className="border rounded-lg px-3 py-2 text-sm w-44"
              value={passIdNumber}
              onChange={(e) => setPassIdNumber(e.target.value)}
            />
          </div>
          <div>
            <label className="text-xs text-gray-500 block mb-1">Mobile</label>
            <input
              className="border rounded-lg px-3 py-2 text-sm w-44"
              value={passMobile}
              onChange={(e) => setPassMobile(e.target.value)}
            />
          </div>
          <button
            type="submit"
            disabled={passBusy}
            className="px-4 py-2 bg-gray-900 text-white rounded-lg text-sm disabled:opacity-50"
          >
            {passBusy ? "Checking…" : "Validate"}
          </button>
        </form>
        {passResult && (
          <div
            className={`mt-4 rounded-lg p-3 text-sm ${
              passResult.valid
                ? "bg-emerald-50 text-emerald-900"
                : "bg-amber-50 text-amber-900"
            }`}
          >
            <p className="font-medium">
              {passResult.valid ? "Pass valid for entry window" : "Not valid"}
            </p>
            {passResult.reasons.length > 0 && (
              <ul className="list-disc pl-4 mt-1 text-xs">
                {passResult.reasons.map((r, i) => (
                  <li key={i}>{r}</li>
                ))}
              </ul>
            )}
            {passResult.visitor && (
              <p className="text-xs mt-2 text-gray-700">
                {String(passResult.visitor.fullName || "")} · zones:{" "}
                {Array.isArray(passResult.visitor.accessZones)
                  ? (passResult.visitor.accessZones as string[]).join(", ")
                  : "—"}
              </p>
            )}
          </div>
        )}
      </div>

      <div>
        <div className="flex flex-wrap justify-between items-center gap-2 mb-3">
          <h2 className="text-lg font-semibold text-gray-900">
            Supported integrations (field / partner)
          </h2>
          <button
            type="button"
            disabled={saving || !settings}
            onClick={saveConnectors}
            className="inline-flex items-center gap-2 px-4 py-2 bg-gray-900 text-white rounded-lg text-sm hover:bg-gray-800 disabled:opacity-50"
          >
            <Save className="w-4 h-4" />
            {saving ? "Saving…" : "Save connectors"}
          </button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {CONNECTOR_DEFS.map((def) => {
            const Icon = def.icon;
            const cfg = settings?.connectors?.[def.id] || {};
            return (
              <div
                key={def.id}
                className="bg-white rounded-lg border border-gray-200 p-4 space-y-2"
              >
                <Icon className="w-6 h-6 text-[#0fa9a6] mb-1" />
                <h3 className="font-semibold text-gray-900 text-sm">
                  {def.title}
                </h3>
                <select
                  className="w-full border rounded-lg px-2 py-1.5 text-xs"
                  value={cfg.status || "partner_setup"}
                  onChange={(e) =>
                    patchConnector(def.id, {
                      status: e.target.value as IntegrationConnectorStatus,
                    })
                  }
                >
                  {STATUS_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
                <input
                  className="w-full border rounded-lg px-2 py-1 text-xs"
                  placeholder="Vendor / model"
                  value={cfg.vendor || ""}
                  onChange={(e) =>
                    patchConnector(def.id, { vendor: e.target.value })
                  }
                />
                <textarea
                  className="w-full border rounded-lg px-2 py-1 text-xs min-h-[48px]"
                  placeholder="Notes"
                  value={cfg.notes || ""}
                  onChange={(e) =>
                    patchConnector(def.id, { notes: e.target.value })
                  }
                />
                <span
                  className={`inline-block text-xs font-medium px-2 py-0.5 rounded ${statusBadgeClass(
                    cfg.status
                  )}`}
                >
                  {cfg.status || "partner_setup"}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm p-5">
        <h2 className="text-lg font-semibold text-gray-900 mb-2">
          Recent notification delivery logs
        </h2>
        <p className="text-xs text-gray-500 mb-4">
          Pulled from the notification delivery log service.
        </p>
        {loading ? (
          <div className="flex justify-center py-12">
            <SpinLoader />
          </div>
        ) : logs.length === 0 ? (
          <p className="text-gray-500 text-sm">No log rows returned.</p>
        ) : (
          <div className="overflow-x-auto max-h-72 overflow-y-auto text-xs">
            <table className="w-full text-left">
              <thead className="sticky top-0 bg-gray-100">
                <tr>
                  <th className="p-2">Time</th>
                  <th className="p-2">Channel</th>
                  <th className="p-2">Recipient</th>
                  <th className="p-2">Status</th>
                </tr>
              </thead>
              <tbody>
                {logs.map((row, i) => (
                  <tr key={(row.id as string) || (row._id as string) || i} className="border-t">
                    <td className="p-2 whitespace-nowrap">
                      {row.createdAt
                        ? new Date(String(row.createdAt)).toLocaleString()
                        : "—"}
                    </td>
                    <td className="p-2">
                      {String(row.channel || row.notificationType || row.type || "—")}
                    </td>
                    <td className="p-2 truncate max-w-[180px]">
                      {String(row.recipient || row.to || "—")}
                    </td>
                    <td className="p-2">{String(row.status || row.result || "—")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default SecurityIntegrations;
